// SummaryTab — renders the latest COMPLETED review run's AI summary for a
// case (summary_text + not_verifiable_items + worksheet_fields) plus a
// "Download Word summary" button in the header. Mirrors the legacy
// CaseDetail.tsx Summary tab render so the new UI (DataWashPage ->
// CaseDetailPane) shows the same content the operator had on the old page.
//
// No data loading here — the parent (CaseDetailPane) loads the
// ReviewRunDetail via listReviewRuns -> getReviewRun and passes `summary`
// down so the tab stays in sync with the case being viewed.

import { useState } from 'react';
import { Loader2, ScrollText, AlertCircle, RefreshCw, Flag } from 'lucide-react';
import type { Case } from '../api/cases';
import type { ReviewSummary, ReviewScore, TriggeredFlag } from '../api/review';
import TierBadge from './TierBadge';
import FlagsPopup from './FlagsPopup';
import { renderMarkdown } from '../utils/markdown';

// Strip the rubric-explanation section ("## 1. Aggregate Score and Tier" --
// the "how scoring works" paragraph) from the summary text before rendering.
// The Summary tab already shows the score + tier in a card, so the textual
// explanation is redundant. UI-scoped -- does not touch the backend summary
// or any export that uses summary_text. (operator 2026-10-21)
function stripRubricExplanation(md: string): string {
  if (!md) return md;
  // Remove from the "## 1." heading up to (not including) the next "## " heading.
  const startIdx = md.search(/^## \d\.\s/m);
  if (startIdx === -1) return md;
  const rest = md.slice(startIdx);
  // Find the second ## heading (skip the first which is the one we're removing).
  const afterFirst = rest.search(/\n## /);
  if (afterFirst === -1) {
    // section 1 is the only section -- drop everything from it onward.
    return md.slice(0, startIdx).trimEnd();
  }
  // Find the heading AFTER section 1 (the next ## that isn't section 1).
  const section2 = rest.slice(afterFirst).search(/^## (?!1\.\s)/m);
  if (section2 === -1) {
    return md.slice(0, startIdx).trimEnd();
  }
  const cutTo = startIdx + afterFirst + section2;
  return (md.slice(0, startIdx) + md.slice(cutTo)).trim();
}
import PathBConsentModal from './PathBConsentModal';



interface SummaryTabProps {
  caseData: Case;
  summary: ReviewSummary | null;
  score: ReviewScore | null;
  flags: TriggeredFlag[];
  onOverrideFlag: (flagId: string, reason: string) => Promise<void>;
  onUnoverrideFlag?: (flagId: string) => Promise<void>;
  loading: boolean;
  onDownloadDocx: () => void;
  downloading: boolean;
  onDownloadFlagMemo: (deterministic?: boolean) => void;
  downloadingFlagMemo: boolean;
  canDownloadFlagMemo: boolean;
  onDownloadFlagMemoClaudeApi: () => void;
  downloadingFlagMemoClaudeApi: boolean;
  latestCompletedRunId?: string | null;
  onRegenerate: () => void;
  onGenerate?: () => void;
  regenerating: boolean;
  reviewInProgress?: boolean;
  rereviewLoading?: boolean;
  error?: string | null;
  onDismissError?: () => void;
}



function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-4">
      <div className="rounded-full bg-slate-100 p-3 mb-3">
        <ScrollText size={22} className="text-slate-400" />
      </div>
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      <p className="text-xs text-slate-500 mt-1">{message}</p>
    </div>
  );
}

export default function SummaryTab({
  caseData,
  summary,
  score,
  flags,
  onOverrideFlag,
  onUnoverrideFlag,
  loading,
  onDownloadFlagMemo,
  downloadingFlagMemo,
  canDownloadFlagMemo,
  latestCompletedRunId,
  onRegenerate,
  onGenerate,
  regenerating,
  reviewInProgress,
  rereviewLoading,
  error,
  onDismissError,
}: SummaryTabProps) {
  const [pathbConsent, setPathbConsent] = useState<{ open: boolean; format: 'docx' | 'pdf' }>(
    { open: false, format: 'docx' },
  );
  // Deterministic (no-LLM) Flag Memorandum production -- when checked the
  // .docx export skips the narrative LLM call and assembles the Bottom line
  // + Recommendation deterministically. Operator request 2026-10-26.
  const [deterministicMemo, setDeterministicMemo] = useState(false);
  // Flags popup (opened from the clickable score / tier card). Holds the
  // per-flag override UI so flags + 'Mark as false positive' live in a
  // popup rather than bolted onto the end of the summary page. On close,
  // if any flag was overridden, the popup asks whether to regenerate the
  // summary. Operator request 2026-10-26.
  const [flagsPopupOpen, setFlagsPopupOpen] = useState(false);

  // Sort flags by severity (AUTO_ESCALATE > HIGH > MEDIUM > LOW) so the
  // most material flags surface first, mirroring the legacy Flags tab.
  const SEVERITY_ORDER: Record<string, number> = { AUTO_ESCALATE: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
  const sortedFlags = [...(flags ?? [])].sort(
    (a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9),
  );

  // Open the flags popup (clickable score / tier badge entry point).
  const openFlagsPopup = () => setFlagsPopupOpen(true);
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400 py-16">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="summary-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800">AI Summary</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={summary ? onRegenerate : (onGenerate ?? onRegenerate)}
            disabled={regenerating || reviewInProgress || rereviewLoading}
            className="btn-secondary text-sm inline-flex items-center gap-2"
            title={!summary ? 'Load public data and run review to generate the summary' : 'Regenerate the AI summary from the latest completed review run'}
            data-testid="summary-regenerate"
          >
            {(regenerating || reviewInProgress || rereviewLoading) ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {summary ? 'Regenerate summary' : 'Generate summary'}
          </button>
          <button
            onClick={() => onDownloadFlagMemo(deterministicMemo)}
            disabled={!canDownloadFlagMemo || downloadingFlagMemo}
            className="btn-secondary text-sm inline-flex items-center gap-2"
            title="Export a senior-analyst Flag Memorandum (.docx) via the local python-docx renderer (Path A, instant)."
            data-testid="summary-download-flag-memo"
          >
            {downloadingFlagMemo ? <Loader2 size={16} className="animate-spin" /> : <ScrollText size={16} />}
            Summary Flag Memorandum (.docx)
          </button>
          <label
            className="inline-flex items-center gap-1.5 text-xs text-slate-600 select-none cursor-pointer"
            title="When checked, produce the memorandum without an LLM call (deterministic assembler). The Bottom line + Recommendation are assembled from the structured facts instead of synthesized by the AI."
          >
            <input
              type="checkbox"
              checked={deterministicMemo}
              onChange={(e) => setDeterministicMemo(e.target.checked)}
              className="rounded border-slate-300 text-primary-600 focus:ring-primary-300"
              data-testid="summary-flag-memo-deterministic"
            />
            Deterministic (no LLM)
          </label>
        </div>
      </div>

      {pathbConsent.open && latestCompletedRunId && (
        <PathBConsentModal
          open={pathbConsent.open}
          runId={latestCompletedRunId}
          caseNumber={caseData.case_number || caseData.id}
          format={pathbConsent.format}
          onClose={() => setPathbConsent({ open: false, format: pathbConsent.format })}
        />
      )}

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span className="flex-1">{error}</span>
          {onDismissError && (
            <button
              onClick={onDismissError}
              className="text-red-500 hover:text-red-700 text-xs underline"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {!summary ? (
        <EmptyState
          title="No summary available"
          message="Run a review to generate the AI summary."
        />
      ) : (
        <div className="space-y-6">
          {score && (
            <button
              type="button"
              onClick={openFlagsPopup}
              title="Click to review flags and mark false positives"
              className="flex items-center gap-4 p-4 rounded-lg bg-slate-50 border border-slate-200 w-full text-left cursor-pointer hover:border-primary-300 hover:bg-primary-50/40 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-800">{score.aggregate_score}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Aggregate Score</p>
              </div>
              <div className="h-12 w-px bg-slate-200" />
              <TierBadge tier={score.tier} size="lg" />
              {sortedFlags.length > 0 && (
                <div className="ml-auto flex items-center gap-1 text-xs font-medium text-primary-600">
                  <Flag size={14} />
                  Review flags / mark false positives ({sortedFlags.length})
                </div>
              )}
            </button>
          )}
          {summary.summary_text && (
            <div className="card p-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Summary</h3>
              <div dangerouslySetInnerHTML={{ __html: renderMarkdown(stripRubricExplanation(summary.summary_text)) }} />
            </div>
          )}
          {summary.not_verifiable_items && summary.not_verifiable_items.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Not Verifiable</h3>
              <ul className="space-y-1">
                {summary.not_verifiable_items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Worksheet Fields hidden per operator direction 2026-10-20. */}
          {/* Triggered Red Flags are shown in a popup opened from the
              clickable score / tier card above (FlagsPopup). The per-flag
              'Mark as false positive' override UI lives there, + on close
              the popup asks whether to regenerate the summary if any flag
              was overridden. Operator 2026-10-26. */}
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
            Case {caseData.case_number} · Generated{' '}
            {summary.generated_at ? new Date(summary.generated_at).toLocaleString() : '—'}
          </p>
        </div>
      )}

      <div className="mt-6 flex items-start gap-2 text-xs text-slate-500" data-testid="ai-disclaimer">
        <AlertCircle size={14} className="shrink-0 mt-0.5" />
        <span>AI-generated summary — AI can make mistakes. Verify before relying.</span>
      </div>

      <FlagsPopup
        open={flagsPopupOpen}
        flags={sortedFlags}
        onOverrideFlag={onOverrideFlag}
        onUnoverrideFlag={onUnoverrideFlag}
        onRegenerate={onRegenerate}
        regenerating={regenerating}
        onClose={() => setFlagsPopupOpen(false)}
      />
    </div>
  );
}